import { Directive, ElementRef, EventEmitter, HostBinding, HostListener, Input, Output } from '@angular/core';

interface FileDropEvent extends DragEvent {
    __wsFileDropHandled?: boolean;
}

/**
 * Turns any element into a file drop zone.
 *
 * Example usage:
 * <div wsFileDrop (filesDropped)="onFilesDropped($event)">
 *     Drop files here
 * </div>
 *
 * The directive adds an `over-drop-target` CSS class while files are being dragged over the host element.
 *
 * **Nesting** – Multiple `wsFileDrop` zones can be nested.  All zones
 * independently track their own drag-over state (the CSS class is applied
 * correctly at every level), but only the *innermost* zone emits
 * `filesDropped` when the user drops files.
 */
@Directive({
    selector: '[wsFileDrop]',
    standalone: true,
})
export class FileDropDirective {
    constructor(elementRef: ElementRef<HTMLElement>) {
        this._el = elementRef.nativeElement;
        this._el.setAttribute(FileDropDirective._marker, '');
    }

    @Input() dropDisabled = false;

    @Output() filesDropped: EventEmitter<File[]> = new EventEmitter<File[]>();

    @HostBinding('class.over-drop-target') isDraggingOver = false;

    /** Attribute used by nested zones to locate the closest drop zone via `closest()`. */
    private static readonly _marker = 'data-ws-file-drop';

    private readonly _el: HTMLElement;

    /**
     * Tracks nested dragenter/dragleave pairs. The browser fires dragleave
     * when the pointer moves from a parent to a child element, immediately
     * followed by dragenter on the child. Counting these transitions lets
     * us keep the class stable until the pointer truly leaves the host.
     */
    private _dragCounter = 0;

    @HostListener('dragover', ['$event'])
    onDragOver(event: DragEvent): void {
        if (this.dropDisabled) { return; }
        event.preventDefault();
        // No stopPropagation — ancestor zones also need dragover to remain valid drop targets.
    }

    @HostListener('dragenter', ['$event'])
    onDragEnter(event: DragEvent): void {
        if (this.dropDisabled) { return; }
        event.preventDefault();
        this._dragCounter++;

        // Check the DOM to see if *this* host is the closest drop zone to
        // the event target.  If a nested zone is closer, yield the highlight.
        this.isDraggingOver = this._isInnermostZoneFor(event.target);
    }

    @HostListener('dragleave', ['$event'])
    onDragLeave(event: DragEvent): void {
        if (this.dropDisabled) { return; }
        event.preventDefault();
        this._dragCounter--;

        if (this._dragCounter === 0) {
            this.isDraggingOver = false;
        }
    }

    @HostListener('drop', ['$event'])
    onDrop(event: DragEvent): void {
        if (this.dropDisabled) { return; }
        event.preventDefault();
        // No stopPropagation — let ancestors receive the drop so they can reset their own drag state.
        this._dragCounter = 0;
        this.isDraggingOver = false;

        // Only the innermost drop zone emits the files.  Once the first
        // directive handles the drop it marks the event; ancestors see the
        // mark and skip the emit, but still reset their visual state above.
        if ((event as FileDropEvent).__wsFileDropHandled) {
            return;
        }
        (event as FileDropEvent).__wsFileDropHandled = true;

        const files: File[] = [];
        if (event.dataTransfer?.files) {
            for (let i = 0; i < event.dataTransfer.files.length; i++) {
                files.push(event.dataTransfer.files[i]);
            }
        }

        if (files.length) {
            this.filesDropped.emit(files);
        }
    }

    /**
     * Returns `true` when this directive's host element is the closest
     * `[data-ws-file-drop]` ancestor (or self) of the given event target.
     * In a nested layout the innermost zone wins, so outer zones yield.
     */
    private _isInnermostZoneFor(target: EventTarget | null): boolean {
        if (!(target instanceof Element)) {
            return true;
        }
        const selector = `[${FileDropDirective._marker}]`;
        const closest = target.closest(selector);
        return closest === this._el;
    }
}
